import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { Download, Settings } from "lucide-react";
import { toast } from "sonner";

import { deleteBatchRun, fetchBatchRunFile, fetchBatchRuns, uploadBatchFile } from "@/services/batchApi";
import { setSearchType } from "@/state/searchInputSlice";
import { useIsBroker } from "@/auth/roles";
import { homepageRoute } from "@/routing";
import {
    ALLOWED_ACCEPT,
    ALLOWED_EXTENSIONS,
    BATCH_STATUS,
    BatchDropzone,
    BatchOutputConfigModal,
    BatchPredictionRow,
    MAX_FILE_SIZE_BYTES,
} from "@/components/dataPages/batch";

const getExtension = (name) => name.substring(name.lastIndexOf(".") + 1).toLowerCase();

const countCsvLines = (text) => {
    if (!text) return 0;
    const trimmed = text.replace(/\n+$/u, "");
    if (!trimmed) return 0;
    const total = trimmed.split(/\r?\n/u).length;
    return Math.max(0, total - 1);
};

const RUN_STATUS = {
    DONE: BATCH_STATUS.DONE,
    WARNING: BATCH_STATUS.WARNING,
    FAILED: BATCH_STATUS.FAILED,
};

const toPrediction = (run) => ({
    id: run.id,
    fileName: run.fileName,
    extension: run.extension,
    status: RUN_STATUS[run.status],
    createdAt: new Date(`${run.createdAt}Z`),
    lineCount: run.rowCount,
    warning: run.message,
    downloadable: run.downloadable,
});

const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    link.click();
    URL.revokeObjectURL(url);
};

export const Batch = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const canAccess = useIsBroker();
    const fileInputRef = useRef(null);
    const [runs, setRuns] = useState([]);
    const [processing, setProcessing] = useState([]);
    const [isConfigOpen, setIsConfigOpen] = useState(false);
    const [uploadingIds, setUploadingIds] = useState(() => new Set());
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);
    const controllersRef = useRef(new Map());

    useEffect(() => {
        dispatch(setSearchType("batch"));
    }, [dispatch]);

    useEffect(() => {
        if (!canAccess) {
            navigate(homepageRoute, { replace: true });
        }
    }, [canAccess, navigate]);

    const loadRuns = useCallback(async () => {
        try {
            const history = await fetchBatchRuns();
            setRuns(history.map(toPrediction));
        } catch {
            toast.error("Could not load recent batch predictions.");
        }
    }, []);

    useEffect(() => {
        if (canAccess) loadRuns();
    }, [canAccess, loadRuns]);

    useEffect(() => {
        const controllers = controllersRef.current;
        return () => controllers.forEach((c) => c.abort());
    }, []);

    const handleFile = useCallback(
        async (file) => {
            if (!file) return;
            const ext = getExtension(file.name);
            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                toast.error("Incorrect format of the file.\nPlease upload the file in XLSX or CSV format.");
                return;
            }
            if (file.size > MAX_FILE_SIZE_BYTES) {
                toast.error("File exceeds maximum size of 50 MB.");
                return;
            }

            const id = `${Date.now()}-${file.name}`;
            const createdAt = new Date();
            const controller = new AbortController();
            controllersRef.current.set(id, controller);
            setUploadingIds((prev) => {
                const next = new Set(prev);
                next.add(id);
                return next;
            });

            let lineCount = null;
            if (ext === "csv") {
                try {
                    lineCount = countCsvLines(await file.text());
                } catch {
                    lineCount = null;
                }
            }

            const transitionToProcessing = () => {
                setUploadingIds((prev) => {
                    if (!prev.has(id)) return prev;
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
                setProcessing((prev) =>
                    prev.some((p) => p.id === id)
                        ? prev
                        : [{ id, fileName: file.name, extension: ext, status: BATCH_STATUS.PROCESSING, createdAt, lineCount }, ...prev],
                );
            };

            try {
                await uploadBatchFile(file, { signal: controller.signal, onUploaded: transitionToProcessing });
                await loadRuns();
            } catch (error) {
                const aborted = error?.name === "CanceledError" || error?.code === "ERR_CANCELED" || controller.signal.aborted;
                if (!aborted) {
                    if (error?.response?.status !== 422) toast.error("Batch job failed. Please try again.");
                    await loadRuns();
                }
            } finally {
                controllersRef.current.delete(id);
                setProcessing((prev) => prev.filter((p) => p.id !== id));
                setUploadingIds((prev) => {
                    if (!prev.has(id)) return prev;
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }
        },
        [loadRuns],
    );

    const handleCancel = (prediction) => {
        const controller = controllersRef.current.get(prediction.id);
        if (controller) controller.abort();
    };

    const handleDownload = async (prediction) => {
        try {
            saveBlob(await fetchBatchRunFile(prediction.id), `estimated_${prediction.fileName}`);
        } catch {
            toast.error("Could not download the file. Please try again.");
        }
    };

    const handleDelete = async (prediction) => {
        try {
            await deleteBatchRun(prediction.id);
            setRuns((prev) => prev.filter((p) => p.id !== prediction.id));
        } catch {
            toast.error("Could not delete the batch prediction. Please try again.");
        }
    };

    const handleClickUpload = () => {
        if (uploadingIds.size > 0) return;
        fileInputRef.current?.click();
    };

    const onInputChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (file) handleFile(file);
    };

    const onDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current += 1;
        setIsDragging(true);
    };

    const onDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) setIsDragging(false);
    };

    const onDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const onDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragging(false);
        if (uploadingIds.size > 0) return;
        const file = event.dataTransfer?.files?.[0];
        if (file) handleFile(file);
    };

    const predictions = [...processing, ...runs];
    const showRecent = predictions.length > 0;
    const isUploading = uploadingIds.size > 0;

    if (!canAccess) return null;

    return (
        <div className="w-full max-w-[1200px] mx-auto flex flex-col gap-6 py-6 px-2">
            <div className="flex flex-col gap-1">
                <h1 className="text-cm-p-text-primary text-[22px] font-medium leading-tight">Batch prediction</h1>
                <p className="text-cm-p-text-secondary text-sm">
                    Predict spot rates for bonus and Forecast contract prices at once by uploading a file
                </p>
            </div>
            <BatchDropzone
                isUploading={isUploading}
                isDragging={isDragging}
                onClick={handleClickUpload}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDragOver={onDragOver}
                onDrop={onDrop}
            />
            <input ref={fileInputRef} type="file" accept={ALLOWED_ACCEPT} className="hidden" onChange={onInputChange} />
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-4">
                    <p className={showRecent ? "text-cm-p-text-primary text-base font-medium" : "text-cm-p-text-secondary text-sm"}>
                        {showRecent ? "Recent batch predictions" : "Once you upload files, rate predictions will show up here"}
                    </p>
                    <div className="flex items-center gap-8">
                        <button
                            type="button"
                            onClick={() => setIsConfigOpen(true)}
                            className="flex items-center gap-2 px-1 text-cm-p-text-primary text-sm font-medium whitespace-nowrap hover:opacity-80 transition-opacity"
                        >
                            <Settings className="size-4 text-cm-p-accent-600" />
                            Output configuration
                        </button>
                        <a
                            href="/files/batch_price_estimate_example.csv"
                            download
                            className="flex items-center gap-2 px-1 text-cm-p-text-primary text-sm font-medium whitespace-nowrap hover:opacity-80 transition-opacity"
                        >
                            <Download className="size-4 text-cm-p-accent-600" />
                            Sample CSV
                        </a>
                    </div>
                </div>
                {showRecent ? (
                    <div className="flex flex-col gap-2">
                        {predictions.map((prediction) => (
                            <BatchPredictionRow
                                key={prediction.id}
                                prediction={prediction}
                                onCancel={handleCancel}
                                onDownload={handleDownload}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                ) : null}
            </div>
            <BatchOutputConfigModal isOpen={isConfigOpen} onClose={() => setIsConfigOpen(false)} />
        </div>
    );
};
